import React, { useState } from "react";
import { AlertTriangle, ChevronRight, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import type { AnalyticsData, Recording, RerunStatus } from "../../api/types";
import { formatDuration } from "../../utils";
import { RecordingActionControls } from "./RecordingControls";
import RerunProgressDialog from "./RerunProgressDialog";

type AnalyticsHeaderProps = {
  data: AnalyticsData;
  /** The session's title, computed the same way everywhere it's cited. */
  title: string;
  mappedSeconds: number;
  personName?: string;
  recording?: Recording;
  archiveBusy?: boolean;
  onArchiveChange?: (id: string, archived: boolean) => void | Promise<void>;
  onRequestDelete?: () => void;
  /** The analysis run picker, for the users who get one. */
  runSelector?: React.ReactNode;
  navigation?: React.ReactNode;
  /** What a re-analysis of this recording is doing, if the viewer may know. */
  rerun?: RerunStatus | null;
  /**
   * Stops the re-analysis. Given only to the users who may stop one — with it
   * the re-run pill opens onto the run's progress, without it it only labels.
   */
  onCancelRerun?: () => Promise<void>;
};

const AnalyticsHeader = ({
  data,
  title,
  mappedSeconds,
  personName,
  recording,
  archiveBusy = false,
  onArchiveChange,
  onRequestDelete,
  runSelector,
  navigation,
  rerun = null,
  onCancelRerun,
}: AnalyticsHeaderProps) => {
  const { t } = useTranslation();
  const [rerunOpen, setRerunOpen] = useState(false);
  const startDate = new Date(Number(data.startTimestamp) * 1000);
  const endDate = new Date(Number(data.endTimestamp) * 1000);
  const startLabel = format(startDate, "h:mm a");
  const endLabel = format(
    endDate,
    startDate.toDateString() === endDate.toDateString() ? "h:mm a" : "MMM d, yyyy · h:mm a",
  );
  const durationLabel = formatDuration(mappedSeconds);
  const isArchived = recording ? !recording.include_in_processes : false;
  const hasRecordingActions = (!!recording && !!onArchiveChange) || !!onRequestDelete;
  const rerunRunning = rerun?.status === "running";
  // A running re-analysis reads as a warning, a failed one as a danger — the
  // colour is the only thing that changes between them.
  const rerunTone = rerunRunning
    ? {
        base: "border-warning/40 bg-warning/10 text-warning-strong",
        hover: "hover:border-warning/60 hover:bg-warning/20",
      }
    : {
        base: "border-danger/40 bg-danger/10 text-danger-strong",
        hover: "hover:border-danger/60 hover:bg-danger/20",
      };
  // The clickable pill borrows the run picker's shape — same height, icon and
  // chevron — so it reads as the control it is rather than as a status word.
  const rerunPillClass = `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${rerunTone.base}`;
  const rerunPillLabel = rerunRunning ? t("rerunning_ellipsis") : t("rerun_failed");
  const rerunPillIcon = rerunRunning ? (
    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
  ) : (
    <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
  );
  // Without a way to stop the run there is nothing behind the pill worth
  // opening, so it stays a label.
  const rerunPillOpens = !!onCancelRerun;

  return (
    <div className="mb-6 flex flex-col items-start justify-between gap-4 @[800px]:flex-row">
      <div className="min-w-0 w-full flex-1">
        <h1 className="font-display text-2xl font-bold text-text">{title}</h1>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-text-dim">
          <span>
            {`${startLabel}–${endLabel} · ${durationLabel} ${t("recorded")}${personName ? ` · ${personName}` : ""}`}
          </span>
          {isArchived && (
            <span className="inline-flex items-center rounded-full border border-info/20 bg-info/10 px-2 py-0.5 text-xs font-medium text-info-strong">
              {t("recording_archived")}
            </span>
          )}
          {runSelector}
          {rerun &&
            (rerunPillOpens ? (
              <button
                type="button"
                onClick={() => setRerunOpen(true)}
                title={t("view_rerun_progress")}
                aria-label={t("view_rerun_progress")}
                className={`${rerunPillClass} cursor-pointer shadow-sm transition ${rerunTone.hover} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/30`}
              >
                {rerunPillIcon}
                <span>{rerunPillLabel}</span>
                <ChevronRight className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
              </button>
            ) : (
              <span
                title={rerun.status === "failed" ? rerun.error_message : undefined}
                className={rerunPillClass}
              >
                {rerunPillIcon}
                {rerunPillLabel}
              </span>
            ))}
        </div>
      </div>
      {(hasRecordingActions || navigation) && (
        <div className="flex shrink-0 items-center gap-2">
          {navigation}
          <RecordingActionControls
            recording={recording}
            archiveBusy={archiveBusy}
            onArchiveChange={onArchiveChange}
            onDelete={onRequestDelete}
          />
        </div>
      )}
      {rerun && rerunOpen && (
        <RerunProgressDialog
          rerun={rerun}
          onClose={() => setRerunOpen(false)}
          onCancel={
            onCancelRerun &&
            (async () => {
              await onCancelRerun();
              setRerunOpen(false);
            })
          }
        />
      )}
    </div>
  );
};

export default AnalyticsHeader;
